import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Package, RefreshCw, AlertCircle, Truck, ExternalLink, MapPin, CreditCard, Tag, Clock, XCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

interface OrderHistoryProps {
  userEmail: string | null;
}

interface TrackingInfo {
  number: string;
  url: string | null;
  company: string | null;
}

interface OrderItem {
  title: string;
  variantTitle: string | null;
  quantity: number;
  sku: string | null;
  originalPrice: string | null;
  discountedPrice: string | null;
  imageUrl: string | null;
}

interface Order {
  id: string;
  orderNumber: string;
  createdAt: string;
  processedAt: string | null;
  financialStatus: string;
  fulfillmentStatus: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  note: string | null;
  email: string | null;
  phone: string | null;
  totalPrice: string;
  currencyCode: string;
  subtotalPrice: string | null;
  totalShipping: string | null;
  totalTax: string | null;
  totalDiscounts: string | null;
  shippingAddress: string | null;
  shippingMethod: string | null;
  shippingCost: string | null;
  trackingInfo: TrackingInfo[] | null;
  fulfillments: Array<{ status: string | null; createdAt: string | null }> | null;
  items: OrderItem[];
}

const OrderHistory = ({ userEmail }: OrderHistoryProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);

  // תרגום סטטוסים לעברית
  const translateStatus = (status: string | null | undefined): string => {
    if (!status) return '';
    const translations: Record<string, string> = {
      // Financial Status
      'PENDING': 'ממתין לתשלום',
      'AUTHORIZED': 'מאושר',
      'PARTIALLY_PAID': 'שולם חלקית',
      'PAID': 'שולם',
      'PARTIALLY_REFUNDED': 'הוחזר חלקית',
      'REFUNDED': 'הוחזר',
      'VOIDED': 'בוטל',
      'EXPIRED': 'פג תוקף',
      // Fulfillment Status
      'UNFULFILLED': 'ממתין למשלוח',
      'PARTIALLY_FULFILLED': 'נשלח חלקית',
      'FULFILLED': 'נשלח',
      'RESTOCKED': 'הוחזר למלאי',
      'ON_HOLD': 'בהמתנה',
      'SCHEDULED': 'מתוזמן',
      'IN_PROGRESS': 'בתהליך',
      // Cancel reasons
      'CUSTOMER': 'בקשת לקוח',
      'FRAUD': 'חשד להונאה',
      'INVENTORY': 'חוסר במלאי',
      'DECLINED': 'תשלום נדחה',
      'OTHER': 'סיבה אחרת',
      // Fulfillment internal status
      'SUCCESS': 'הושלם',
      'OPEN': 'פתוח',
      'CANCELLED': 'בוטל',
      'ERROR': 'שגיאה',
      'FAILURE': 'נכשל',
    };
    return translations[status] || status;
  };

  const fetchOrders = async () => {
    if (!userEmail) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('get-customer-orders');

      if (invokeError) {
        throw invokeError;
      }

      // Check for specific error types from backend
      if (data?.error) {
        if (data.error === 'shopify_auth_failed') {
          setError({
            type: 'auth',
            message: data.message || 'לא ניתן להתחבר לשירות ההזמנות כרגע'
          });
          return;
        }
        throw new Error(data.message || data.error);
      }

      setOrders(data.orders || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError({
        type: 'general',
        message: 'שגיאה בטעינת היסטוריית ההזמנות'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [userEmail]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  const formatShortDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('he-IL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  };

  const hasDiscount = (item: OrderItem) => {
    if (!item.originalPrice || !item.discountedPrice) return false;
    return parseFloat(item.originalPrice) > parseFloat(item.discountedPrice);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!userEmail) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">היסטוריית הזמנות</CardTitle>
          <CardDescription className="text-right">
            כאן תוצג היסטוריית ההזמנות שלך
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              נא להתחבר כדי לראות את ההזמנות שלך
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">היסטוריית הזמנות</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <p className="text-muted-foreground mb-4">{error.message}</p>
            <Button 
              variant="outline" 
              onClick={fetchOrders}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              נסה שוב
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-right font-light">היסטוריית הזמנות</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              אין הזמנות להצגה
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              הזמנות יופיעו כאן לאחר ביצוע רכישה
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-right font-light">היסטוריית הזמנות</CardTitle>
          <CardDescription className="text-right">{orders.length} הזמנות</CardDescription>
        </div>
        <Button variant="ghost" size="icon" onClick={fetchOrders}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {orders.map((order) => (
            <div
              key={order.id}
              className="border rounded-lg overflow-hidden"
            >
              {/* Order Header */}
              <div className="bg-muted/30 p-4">
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div className="text-right flex-1 min-w-0">
                    <p className="font-semibold text-lg">{order.orderNumber}</p>
                    {formatShortDate(order.createdAt) && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatShortDate(order.createdAt)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {order.financialStatus && (
                      <Badge variant={order.financialStatus === 'PAID' ? 'default' : 'outline'}>
                        {translateStatus(order.financialStatus)}
                      </Badge>
                    )}
                    {order.fulfillmentStatus && (
                      <Badge variant={order.fulfillmentStatus === 'FULFILLED' ? 'secondary' : 'outline'}>
                        {translateStatus(order.fulfillmentStatus)}
                      </Badge>
                    )}
                    {order.cancelledAt && (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        בוטל
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Cancellation Info */}
                {order.cancelledAt && (
                  <div className="mt-2 p-2 bg-destructive/10 rounded text-sm text-right">
                    <span className="text-destructive">בוטל ב-{formatShortDate(order.cancelledAt)}</span>
                    {order.cancelReason && (
                      <span className="text-muted-foreground"> - {translateStatus(order.cancelReason)}</span>
                    )}
                  </div>
                )}

                {/* Order Note */}
                {order.note && (
                  <div className="mt-2 p-2 bg-accent/50 rounded text-sm text-right">
                    <span className="font-medium">הערה: </span>
                    {order.note}
                  </div>
                )}
              </div>
              
              {/* Order Items */}
              <div className="p-4">
                <div className="space-y-3">
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      {item.imageUrl ? (
                        <img 
                          src={item.imageUrl} 
                          alt={item.title}
                          className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center flex-shrink-0">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 text-right">
                        <p className="font-medium truncate">{item.title}</p>
                        {item.variantTitle && item.variantTitle !== 'Default Title' && (
                          <p className="text-sm text-muted-foreground">{item.variantTitle}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          <span className="text-sm text-muted-foreground">כמות: {item.quantity}</span>
                          {item.sku && (
                            <span className="text-xs text-muted-foreground">מק"ט: {item.sku}</span>
                          )}
                        </div>
                        {/* Item Price */}
                        {(item.originalPrice || item.discountedPrice) && (
                          <div className="flex items-center gap-2 mt-1">
                            {hasDiscount(item) ? (
                              <>
                                <span className="text-sm font-medium text-primary">
                                  {formatPrice(item.discountedPrice!, order.currencyCode)}
                                </span>
                                <span className="text-xs line-through text-muted-foreground">
                                  {formatPrice(item.originalPrice!, order.currencyCode)}
                                </span>
                              </>
                            ) : (
                              <span className="text-sm">
                                {formatPrice(item.discountedPrice || item.originalPrice!, order.currencyCode)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping & Tracking - only show if there's shipping data */}
              {(order.shippingAddress || order.shippingMethod || (order.trackingInfo && order.trackingInfo.length > 0)) && (
                <>
                  <Separator />
                  <div className="p-4 space-y-3">
                    {/* Shipping Address */}
                    {order.shippingAddress && (
                      <div className="flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="text-right">
                          <span className="font-medium">כתובת משלוח: </span>
                          <span className="text-muted-foreground">{order.shippingAddress}</span>
                        </div>
                      </div>
                    )}

                    {/* Shipping Method */}
                    {order.shippingMethod && (
                      <div className="flex items-center gap-2 text-sm">
                        <Truck className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="text-right">
                          <span className="font-medium">שיטת משלוח: </span>
                          <span className="text-muted-foreground">{order.shippingMethod}</span>
                          {order.shippingCost && parseFloat(order.shippingCost) > 0 && (
                            <span className="text-muted-foreground"> ({formatPrice(order.shippingCost, order.currencyCode)})</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Tracking Info */}
                    {order.trackingInfo && order.trackingInfo.length > 0 && (
                      <div className="bg-primary/5 rounded-md p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Truck className="h-4 w-4 text-primary" />
                          <span className="text-sm font-medium">מעקב משלוח</span>
                        </div>
                        {order.trackingInfo.map((tracking, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <div className="text-right">
                              {tracking.company && (
                                <span className="text-muted-foreground">{tracking.company}: </span>
                              )}
                              <span className="font-mono">{tracking.number}</span>
                            </div>
                            {tracking.url && (
                              <a 
                                href={tracking.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-primary hover:underline flex items-center gap-1"
                              >
                                <span>עקוב</span>
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <Separator />

              {/* Price Summary */}
              <div className="p-4 bg-muted/20">
                <div className="space-y-1 text-sm">
                  {order.subtotalPrice && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">סכום ביניים</span>
                      <span>{formatPrice(order.subtotalPrice, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalShipping && parseFloat(order.totalShipping) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">משלוח</span>
                      <span>{formatPrice(order.totalShipping, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalTax && parseFloat(order.totalTax) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">מע"מ</span>
                      <span>{formatPrice(order.totalTax, order.currencyCode)}</span>
                    </div>
                  )}
                  {order.totalDiscounts && parseFloat(order.totalDiscounts) > 0 && (
                    <div className="flex justify-between text-primary">
                      <span className="flex items-center gap-1">
                        <Tag className="h-3 w-3" />
                        הנחה
                      </span>
                      <span>-{formatPrice(order.totalDiscounts, order.currencyCode)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2">
                    <span>סה"כ</span>
                    <span>{formatPrice(order.totalPrice, order.currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default OrderHistory;